import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ClientInvoicePaymentRemindersSection } from './ClientInvoicePaymentRemindersSection';
import { ClientInvoice } from './useData';
import { cn } from '../../lib/utils';
import { Button, Card, XIcon } from '../../components/ui';

export type ClientInvoicePaymentRemindersPanelVariant = 'panel' | 'slideover' | 'modal';

export interface ClientInvoicePaymentRemindersPanelProps {
  open: boolean;
  onClose: () => void;
  invoice: ClientInvoice | null;
  onReminderAdded?: () => void;
  asPanel?: boolean;
  variant?: ClientInvoicePaymentRemindersPanelVariant;
}

const ClientInvoicePaymentRemindersPanel: React.FC<ClientInvoicePaymentRemindersPanelProps> = ({
  open,
  onClose,
  invoice,
  onReminderAdded,
  asPanel = false,
  variant: variantProp,
}) => {
  const { colors } = useTheme();
  if (!open) return null;

  const variant: ClientInvoicePaymentRemindersPanelVariant = variantProp ?? (asPanel ? 'panel' : 'slideover');
  const showBackdrop = variant === 'slideover' || variant === 'modal';
  const isModal = variant === 'modal';
  const invoiceId = invoice?.invoiceId;

  const content = (
    <>
      <div className={cn('flex-shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b', colors.border, colors.headerBg)}>
        <div className="min-w-0 flex-1 flex items-start gap-3">
          <div className={cn('flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center', colors.surfaceHover)}>
            <svg className={cn('w-5 h-5', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className={cn('text-lg font-semibold', colors.text)}>Payment reminders</h2>
            {invoice && (
              <>
                <p className={cn('mt-0.5 text-sm truncate', colors.textSecondary)} title={invoice.InvoiceNo ?? undefined}>{invoice.InvoiceNo || `Invoice #${invoice.invoiceId}`}</p>
                {invoice.client?.clientName && <p className={cn('text-xs mt-0.5', colors.textMuted)}>Client: {invoice.client.clientName}</p>}
              </>
            )}
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="flex-shrink-0">
          <XIcon className="w-5 h-5" />
        </Button>
      </div>
      <div className={cn('flex-1 overflow-y-auto p-5 min-h-0')}>
        {invoiceId != null ? (
          <ClientInvoicePaymentRemindersSection invoiceId={invoiceId} onReminderAdded={onReminderAdded} variant="panel" />
        ) : (
          <Card variant="outlined" className={cn('rounded-xl border-dashed p-8 text-center', colors.border)}>
            <p className={cn('text-sm', colors.textMuted)}>Select an invoice to view payment reminders.</p>
          </Card>
        )}
      </div>
    </>
  );

  const dialogBox = (
    <div
      className={cn(
        'flex flex-col',
        colors.surface,
        colors.border,
        'border rounded-xl shadow-2xl',
        variant === 'panel' && 'h-full min-h-0 w-full',
        variant === 'slideover' && 'fixed top-0 right-0 bottom-0 z-50 w-full max-w-xl border-l animate-in slide-in-from-right duration-300',
        isModal && 'w-full max-w-lg max-h-[85vh] z-50'
      )}
      role="dialog"
      aria-label="Payment reminders"
    >
      {content}
    </div>
  );

  return (
    <>
      {showBackdrop && <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden />}
      {isModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
          {dialogBox}
        </div>
      ) : (
        dialogBox
      )}
    </>
  );
};

export default ClientInvoicePaymentRemindersPanel;
